import React, { FC } from 'react';
import { Box, useBreakpointValue, useTheme } from '@chakra-ui/react';
import { motion } from 'motion/react';
import Button from 'ts/common/components/gallery/Button';
import CoverPhoto from 'ts/common/components/gallery/CoverPhoto';
import GalleryHeading from 'ts/common/components/gallery/GalleryHeading';
import StudioHeading from 'ts/common/components/gallery/StudioHeading';
import { ILayoutProps } from './BaseLayout';
import { AnimationConfig, borderThickAnimations, getAnimationProps } from './animations';

const Border: FC<{
    borderConfig?: AnimationConfig;
    colorScheme: 'dark' | 'light';
    enableAnimations?: boolean;
}> = ({ borderConfig, colorScheme, enableAnimations = true }) => {
    const theme = useTheme();
    const borderColor = colorScheme === 'dark' ? theme.colors.gray[700] : theme.colors.white;

    return (
        <motion.div
            style={{
                position: 'absolute',
                inset: 0,
                border: '24px solid',
                borderColor,
                pointerEvents: 'none'
            }}
            {...(borderConfig
                ? getAnimationProps(borderConfig, enableAnimations)
                : { initial: false, animate: false })}
        />
    );
};

const BorderThickLayout: FC<ILayoutProps> = ({
    galleryTitle,
    brandName,
    coverPhoto,
    coverPhotoUrl,
    buttonText,
    buttonTextLarge,
    colorScheme,
    onClick,
    enableAnimations = true
}) => {
    const galleryHeadingSize =
        useBreakpointValue({ base: 'sm' as const, lg: 'lg' as const }) ?? 'sm';
    const padding = useBreakpointValue({ base: 6, lg: 8 }) ?? 6;

    return (
        <Box position="absolute" width="full" height="full" onClick={onClick}>
            <Box position="relative" width="full" height="full" overflow="hidden">
                {/* Cover image */}
                <motion.div
                    style={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%'
                    }}
                    {...getAnimationProps(borderThickAnimations.coverImage, enableAnimations)}
                >
                    <CoverPhoto coverPhoto={coverPhoto} coverPhotoUrl={coverPhotoUrl} />
                </motion.div>

                {/* Overlay */}
                <Box
                    position="absolute"
                    width="full"
                    height="full"
                    background={colorScheme === 'dark' ? 'whiteAlpha.400' : 'blackAlpha.500'}
                />

                {/* Content overlay */}
                <Box
                    position="absolute"
                    width="full"
                    height="full"
                    display="flex"
                    alignItems="center"
                    flexDirection="column"
                    paddingX={padding}
                    paddingY={6}
                    textAlign="center"
                    justifyContent="space-between"
                >
                    {/* Studio name at top */}
                    <motion.div
                        {...getAnimationProps(borderThickAnimations.studioName, enableAnimations)}
                    >
                        <Box paddingTop={4}>
                            <StudioHeading text={brandName} variant={colorScheme} />
                        </Box>
                    </motion.div>

                    {/* Gallery name and button centered */}
                    <Box display="flex" flexDirection="column" alignItems="center" gap={6}>
                        <motion.div
                            {...getAnimationProps(
                                borderThickAnimations.galleryName,
                                enableAnimations
                            )}
                        >
                            <GalleryHeading
                                size={galleryHeadingSize}
                                text={galleryTitle}
                                variant={colorScheme}
                            />
                        </motion.div>
                        <motion.div
                            {...getAnimationProps(borderThickAnimations.viewBtn, enableAnimations)}
                        >
                            <Button
                                text={buttonText}
                                textLarge={buttonTextLarge}
                                variant={`secondary ${colorScheme}` as const}
                            />
                        </motion.div>
                    </Box>
                    <Box />
                </Box>
            </Box>

            {/* Border - rendered last to be on top */}
            <Border
                borderConfig={borderThickAnimations.border}
                colorScheme={colorScheme}
                enableAnimations={enableAnimations}
            />
        </Box>
    );
};

export default BorderThickLayout;
